import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlassian/wac-ufo-segment";

import {
    DocumentsIcon,
    GroupsIcon,
    OfficeBuildingIcon,
    PlayIcon
} from "@/components/adg4/icons";
import {
    InfoCard,
    InfoCardProps
} from "@/components/adg4/info-card/src/info-card";
import { useLocalizedHref } from "@/libraries/web-framework";

import { feelSupportedSectioni18n } from "./messages";

const feelSupportedSectionStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBlockStart: 40,
    marginBlockEnd: 32,
    paddingInlineStart: 24,
    paddingInlineEnd: 24
});

const feelSupportedSectionHeaderStyles = css({
    marginBlockEnd: "52px",

    [media.below.md]: {
        textAlign: "center"
    }
});

const infoCardContainerStyles = css({
    display: "flex",
    margin: "0 auto",
    flexWrap: "wrap",
    gap: "24px",
    maxWidth: 1192,
    width: "100%"
});

const cardGroup = css({
    display: "flex",
    flex: "1 1 auto",
    gap: "24px",
    justifyContent: "center",
    flexWrap: "wrap"
});

type CardProps = {
    id: string;
    eventProps?: {
        label?: string;
        labelEnglish?: string;
    };
} & InfoCardProps;

const FeelSupportedSection = () => {
    const intl = useIntl();

    const infoCards1: CardProps[] = [
        {
            id: "atlassianCommunity",
            Icon: GroupsIcon,
            title: intl.formatMessage(
                feelSupportedSectioni18n.atlassianCommunityInfoCardTitle
            ),
            paragraph: intl.formatMessage(
                feelSupportedSectioni18n.atlassianCommunityInfoCardParagraph
            ),
            // callToActionText: intl.formatMessage(
            //     feelSupportedSectioni18n.atlassianCommunityInfoCardCallToActionText
            // ),
            callToActionUrl:
                "https://community.atlassian.com/t5/Confluence/ct-p/confluence",
            eventProps: {
                label: "atlassian-community",
                labelEnglish: "Atlassian Community"
            }
        },
        {
            id: "useCases",
            Icon: OfficeBuildingIcon,
            title: intl.formatMessage(
                feelSupportedSectioni18n.useCasesInfoCardTitle
            ),
            paragraph: intl.formatMessage(
                feelSupportedSectioni18n.useCasesInfoCardParagraph
            ),
            // callToActionText: intl.formatMessage(
            //     feelSupportedSectioni18n.useCasesInfoCardCallToActionText
            // ),
            callToActionUrl: useLocalizedHref("/software/confluence/use-cases"),
            eventProps: {
                label: "use-cases",
                labelEnglish: "Use cases"
            }
        }
    ];
    const infoCards2 = [
        {
            id: "videoWalkthroughs",
            Icon: PlayIcon,
            title: intl.formatMessage(
                feelSupportedSectioni18n.videoWalkthroughsInfoCardTitle
            ),
            paragraph: intl.formatMessage(
                feelSupportedSectioni18n.videoWalkthroughsInfoCardParagraph
            ),
            // callToActionText: intl.formatMessage(
            //     feelSupportedSectioni18n.videoWalkthroughsInfoCardCallToActionText
            // ),
            callToActionUrl: useLocalizedHref("/software/confluence/demo"),
            eventProps: {
                label: "video-walkthroughs",
                labelEnglish: "Video walkthroughs"
            }
        },
        {
            id: "productGuides",
            Icon: DocumentsIcon,
            title: intl.formatMessage(
                feelSupportedSectioni18n.productGuidesInfoCardTitle
            ),
            paragraph: intl.formatMessage(
                feelSupportedSectioni18n.productGuidesInfoCardParagraph
            ),
            // callToActionText: intl.formatMessage(
            //     feelSupportedSectioni18n.productGuidesInfoCardCallToActionText
            // ),
            callToActionUrl: useLocalizedHref(
                "/software/confluence/resources/guides"
            ),
            eventProps: {
                label: "product-guides",
                labelEnglish: "Product guides"
            }
        }
    ];

    return (
        <UFOSegment name="confluence-pt-cfind-1234-feelSupportedSection">
            <section css={feelSupportedSectionStyles}>
                <h2 css={feelSupportedSectionHeaderStyles}>
                    {intl.formatMessage(
                        feelSupportedSectioni18n.feelSupportSectionHeader
                    )}
                </h2>
                <div css={infoCardContainerStyles}>
                    <div css={cardGroup}>
                        {infoCards1.map(
                            ({ eventProps, id, ...infoCardProps }) => (
                                <InfoCard
                                    key={id}
                                    {...infoCardProps}
                                    dataEventProps={{
                                        "data-product-key": "confluence",
                                        "data-event": "clicked",
                                        "data-event-component": "card",
                                        "data-event-container":
                                            "additionalResourcesSection",
                                        "data-label": eventProps?.label,
                                        "data-label-english":
                                            eventProps?.labelEnglish
                                    }}
                                />
                            )
                        )}
                    </div>
                    <div css={cardGroup}>
                        {infoCards2.map(
                            ({ eventProps, id, ...infoCardProps }) => (
                                <InfoCard
                                    key={id}
                                    {...infoCardProps}
                                    dataEventProps={{
                                        "data-product-key": "confluence",
                                        "data-event": "clicked",
                                        "data-event-component": "card",
                                        "data-event-container":
                                            "additionalResourcesSection",
                                        "data-label": eventProps?.label,
                                        "data-label-english":
                                            eventProps?.labelEnglish
                                    }}
                                />
                            )
                        )}
                    </div>
                </div>
            </section>
        </UFOSegment>
    );
};

FeelSupportedSection.displayName = "FeelSupportedSection";

export default FeelSupportedSection;
